<template>
  <WCasinoCard
    :class="platform"
    class="game-card-wrapper"
    :label="game.name"
    :extra-label="game.provider"
    :image-url="game.image"
    :selected="isSelected"
    :loading="isCurrentGameLounching"
    :blocked="notAvailableInRegion"
    :blocking-reason="t('casino.notAvailableInRegion')"
    :toggle-button="isAuthenticated"
    :toggle-button-props="toogleButtonProps"
    :counter="gameCounter"
    :badges="gameBadges"
    :demo-button-props="demoButtonProps"
    :hovered="isWithHover"
    :additional-badge="currencyLimit"
    :type="type === 'bonus' ? 'select' : 'default'"
    data-t="game-card"
    :data-game-id="game.gameId"
    @demo="router.push(demoLink)"
    @play="router.push(realLink)"
  >
    <template v-if="linkByPlatform" #link>
      <NuxtI18nLink v-bind="linkByPlatform" />
    </template>
    <template v-if="isInCasino" #blocked-link>
      <NuxtI18nLink
        :to="NOT_AVAILABLE_LINK"
        external
        target="_blank"
        data-t="how-change-itq2"
        class="how-change"
      >
        {{ t('casino.howChangeRegion') }}
      </NuxtI18nLink>
    </template>
    <template v-if="isEnabledGameStartAnimation" #header>
      <component
        :is="animationComponent"
        :image="game.image"
        :game-id="game.gameId"
        :is-animated="isAnimated"
      />
    </template>
  </WCasinoCard>
</template>

<script lang="ts" setup>
import { useCasinoDeps } from '@st/casino/useDeps'
import type { Game } from '../../types'
import useGameCard from './useGameCard'
import { useGameCardLimits } from './useGameCardLimits'
import { useGameOpen } from '../../composables/useGameOpen'
import { useFavoriteGame } from '../../composables/useFavoriteGame'

export interface Props {
  game: Game
  showPlayerCount?: boolean
  isSelected?: boolean
  type?: 'casino' | 'bonus' | 'casino-search'
}

const {
  game,
  isSelected,
  type = 'casino',
  showPlayerCount = true,
} = defineProps<Props>()

const { t } = useI18n()
const router = useRouter()
const { isDesktop, platform } = usePlatform()

const { isAuthenticated } = useCasinoDeps()

const {
  labels,
  showPlayers,
  notAvailableInRegion,
  demoLink,
  realLink,
  NOT_AVAILABLE_LINK,
} = useGameCard(
  toRef(() => game),
  toRef(() => showPlayerCount),
)

const { playersLimits, currencyLimit } = useGameCardLimits(() => game.gameId)

const { isCurrentGameLounching, isAnimated } = useGameOpen(
  toRef(() => game.gameId),
)

const isInCasino = computed(() => type === 'casino' || type === 'casino-search')
const isInSearch = computed(() => type === 'casino-search')
const isInBonus = computed(() => type === 'bonus')

const linkByPlatform = computed(() => {
  if (isInBonus.value) return undefined
  if (isDesktop.value) {
    return notAvailableInRegion.value
      ? {
          to: NOT_AVAILABLE_LINK.value,
          target: '_blank',
        }
      : {
          to: realLink.value,
          target: '',
        }
  }
  return {
    to: realLink.value,
    target: '',
  }
})

const gameCounter = computed(() => {
  if (!playersLimits.value && (!showPlayers.value || !isInCasino.value))
    return undefined

  return {
    counter: playersLimits.value || game.playersCount,
    subtitle: t('gameCard.plays'),
  }
})

const { handleFavoriteClick, isFavoriteGame, favoriteStoreLoadingStatus } =
  useFavoriteGame(computed(() => game.gameId))

const toogleButtonProps = computed(() => ({
  modelValue: isFavoriteGame.value,
  onToggle: handleFavoriteClick,
  loading: favoriteStoreLoadingStatus.value === 'pending',
}))

const isWithBadges = computed(
  () =>
    labels.value.length &&
    !notAvailableInRegion.value &&
    !isInSearch.value &&
    !isInBonus.value,
)
const gameBadges = computed(() => {
  if (!isWithBadges.value) return []

  return labels.value
})

const demoButtonProps = computed(() => {
  if (game.hasDemoMode) {
    return {
      text: t('gameCard.demoMode'),
    }
  }
  return undefined
})

const isWithHover = computed(
  () => isInCasino.value && !isCurrentGameLounching.value && isDesktop.value,
)

const isEnabledGameStartAnimation = computed(
  () => type === 'casino' && isAnimated.value,
)

const animationComponent = computed(() =>
  isDesktop.value
    ? defineAsyncComponent(() => import('./GameCardAnimation.vue'))
    : defineAsyncComponent(() => import('./MGameCardAnimation.vue')),
)
</script>
<style scoped>
.how-change {
  margin: 0;
  font: var(--desktop-text-xs-medium);
  color: var(--button-primary-default);
  text-decoration: none;
}

/* пока не понятно, как переопределить цвет рамки бейджа в ДС */
.game-card-wrapper {
  :deep(.w-badge) {
    border-color: transparent !important;
  }
}
</style>
